import type React from 'react'

import { cn } from '@modules/lib/lib'

type ImageSlot = {
  content: React.ReactNode
  badge?: React.ReactNode
  /** キャンバス右上に重ねるフローティングツールバー等 */
  toolbar?: React.ReactNode
  /**
   * キャンバス右下に重ねる「元のサイズに戻す」ボタン等（issue #2439）。
   * 内箱（負マージンで高さを詰める側）ではなく外枠に置くため、縮小表示でも見切れない
   */
  zoomControl?: React.ReactNode
  containerRef?: React.Ref<HTMLDivElement>
  /**
   * 現在のズーム倍率。1 未満のとき、縮小した見た目に合わせて外箱のレイアウト高も縮める。
   * CSS transform はレイアウト高を変えないため、これが無いと縮小しても
   * 「高いままの枠の中央に小さい画像が浮く」だけでページ全体の高さが縮まない。
   */
  heightScale?: number
  /**
   * 計測済みの内箱の高さ (px、ズーム倍率を掛ける前のレイアウト高)。
   * heightScale と併せて負マージン量の算出に使う。
   * 画像の内在ピクセル数 (`img.naturalHeight`) とは別物なので混同しないこと。
   */
  contentHeight?: number
}

interface SimulationImagePanelProps {
  layout: 'vertical' | 'horizontal'
  original?: ImageSlot
  processed?: ImageSlot
  /** 画像パネルの背景（画像外）がクリックされた際のコールバック */
  onBackgroundClick?: () => void
}

/**
 * 内箱に当てる上下の負マージン量を返す。
 *
 * 内箱の高さ H は倍率に依らず一定（= 計測対象として不変。ResizeObserver を
 * 発火させないので getMetrics() の座標計算に一切影響しない）で、上下に
 * `-H * (1 - s) / 2` を当てることで外箱の auto 高だけが `H * s` になる。
 * 縮小された画像は react-zoom-pan-pinch が内箱の中央に固定する
 * (centerZoomedOut 未指定時は bounds の min === max) ため、外箱の可視領域と
 * ちょうど一致する。
 *
 * s は 1 で上限クランプする。等倍・拡大 (heightScale >= 1) では 0 を返し、
 * 外箱を広げない（はみ出しは従来どおり overflow-hidden で切る）。
 */
const getCollapseMargin = (heightScale: number | undefined, contentHeight: number | undefined) => {
  const scale = Math.min(heightScale ?? 1, 1)
  if (!contentHeight || scale >= 1) return 0
  return (contentHeight * (1 - scale)) / 2
}

export const SimulationImagePanel: React.FC<SimulationImagePanelProps> = ({
  layout,
  original,
  processed,
  onBackgroundClick,
}) => {
  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // 画像コンテナ自体のクリック（子要素を除く）でのみコールバックを呼ぶ
    if (e.target === e.currentTarget && onBackgroundClick) {
      onBackgroundClick()
    }
  }

  const renderSlot = (slot: ImageSlot) => {
    const margin = getCollapseMargin(slot.heightScale, slot.contentHeight)
    return (
      // 内箱を挟んだことで内側の h-full が auto に解決されるようになり、min-h-[280px]
      // より低い内容（横長画像・空状態）が上寄せになる。justify-center で従来の
      // 縦中央揃えを保つ（回帰テスト: story の ShortContentCentered）
      <div className="relative flex flex-col justify-center flex-1 min-h-[280px] overflow-hidden rounded">
        <div
          ref={slot.containerRef}
          className="w-full"
          style={margin ? { marginTop: -margin, marginBottom: -margin } : undefined}
        >
          {slot.content}
        </div>
        {slot.badge}
        {slot.toolbar}
        {slot.zoomControl ? (
          // max-w: ズーム操作が複数ボタンになったので、狭い幅では折り返せるように上限を与える
          // （与えないと absolute の内容幅がスロット幅を超え、overflow-hidden で切れる）
          <div className="absolute bottom-2 right-2 z-10 max-w-[calc(100%-1rem)]">
            {slot.zoomControl}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      // align-items は既定の stretch のまま。行の cross size は各スロットの
      // 内容高（縮小後）から決まるので、単独表示・両方縮小のいずれでも縮小は効く。
      // stretch を維持することで、左右スロットの高さが常に揃う（ロード中の段差を防ぐ）
      className={cn('flex gap-3', layout === 'vertical' ? 'flex-col-reverse' : 'flex-row')}
      onClick={handlePanelClick}
    >
      {original ? renderSlot(original) : null}
      {processed ? renderSlot(processed) : null}
    </div>
  )
}
